import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Check, Fingerprint, LogOut as OutIcon, Loader2, Clock3, MapPin } from "lucide-react";
import { useT, taskTitle } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, errMsg, getPosition, fmtTime, fmtDate, fmtMin } from "@/lib/api";
import { RequestBox } from "@/components/RequestBox";
import { Card, GeoBadge, Bar, Loader, PositionBadge } from "@/components/kv";

const ShiftCard = ({ entry }) => {
  const { t } = useT();
  return (
    <Card className="p-5 flex items-center justify-between gap-4" data-testid="staff-shift-card">
      <div>
        <div className="font-mono-kv text-[11px] uppercase tracking-widest text-[#99432b]">{t("shift.today")}</div>
        {entry.type === "shift" ? (
          <div className="font-mono-kv text-3xl font-semibold mt-1" data-testid="staff-shift-hours">{entry.start} – {entry.end}</div>
        ) : (
          <div className="font-display text-2xl font-bold mt-1" data-testid="staff-shift-hours">
            {entry.type === "leave" ? `${t("shift.leave")} · ${t(`leaves.types.${entry.leave_type}`)}` : t("shift.off")}
          </div>
        )}
      </div>
      {entry.custom && <span className="rounded-full bg-amber-100 text-amber-900 text-xs px-2.5 py-1 font-semibold">{t("shift.custom")}</span>}
    </Card>
  );
};

const useNowTick = () => {
  const [, setN] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setN((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
};

const ClockCard = ({ data, onDone }) => {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  useNowTick();
  const open = data.open_record;
  const doneToday = !open && data.records.length > 0;
  const s = data.settings;

  const act = async (kind) => {
    setBusy(true);
    try {
      const pos = await getPosition();
      const { data: rec } = await api.post(`/me/clock-${kind}`, pos);
      if (kind === "out") toast.success(t("clock.ok_out"));
      else if (rec.in_verified) toast.success(t("clock.ok_in"));
      else toast.warning(t("clock.ok_in_outside", { d: rec.in_distance_m }));
      onDone();
    } catch (e) {
      toast.error(e.geo ? t(`clock.geo_${e.geo}`) : errMsg(e, t));
    } finally {
      setBusy(false);
    }
  };

  const workedNow = open ? Math.max(0, Math.floor((Date.now() - new Date(open.clock_in).getTime()) / 60000)) : 0;

  return (
    <Card className="p-5 sm:p-6 space-y-5 overflow-hidden relative" data-testid="clock-card">
      <div className="flex items-center gap-3">
        <span className={`relative h-3 w-3 rounded-full ${open ? "bg-green-600 kv-breathe" : "bg-stone-400"}`} />
        <span className="font-semibold" data-testid="clock-state-text">
          {open ? t("clock.in_now") : doneToday ? t("clock.done_today") : t("clock.not_in")}
        </span>
      </div>
      {open && (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-[#F3EDE2] p-3">
            <div className="text-xs text-stone-500">{t("clock.since")}</div>
            <div className="font-mono-kv text-2xl font-semibold" data-testid="clock-in-time">{fmtTime(open.clock_in)}</div>
          </div>
          <div className="rounded-xl bg-[#F3EDE2] p-3">
            <div className="text-xs text-stone-500">{t("clock.worked")}</div>
            <div className="font-mono-kv text-2xl font-semibold" data-testid="clock-worked">{fmtMin(workedNow, t)}</div>
          </div>
          <div className="col-span-2 flex flex-wrap gap-2 items-center">
            <GeoBadge verified={open.in_verified} distance={open.in_distance_m} testid="clock-geo-badge" />
            {open.overtime_min > 0 && (
              <span className="rounded-full bg-orange-100 text-orange-800 text-xs px-2.5 py-0.5 font-semibold">{t("clock.overtime_now")}: {fmtMin(open.overtime_min, t)}</span>
            )}
          </div>
        </div>
      )}
      <button
        disabled={busy}
        onClick={() => act(open ? "out" : "in")}
        data-testid={open ? "clock-out-button" : "clock-in-button"}
        className={`w-full h-20 rounded-2xl text-lg font-bold flex items-center justify-center gap-3 shadow-md transition-[transform,background-color] active:scale-[0.98] disabled:opacity-70 ${open ? "bg-[#2c1810] hover:bg-[#3d2418] text-amber-50" : "bg-[#b8563a] hover:bg-[#99432b] text-white"}`}
      >
        {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : open ? <OutIcon className="h-6 w-6" /> : <Fingerprint className="h-6 w-6" />}
        {busy ? t("clock.locating") : open ? t("clock.out_btn") : t("clock.in_btn")}
      </button>
      <div className="text-xs text-stone-500 space-y-1">
        <p className="flex gap-1.5"><MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />{t("clock.cafe_location")}: {s.address} · {t("clock.radius")}: {s.radius_m} m</p>
        <p>{t("clock.gps_note")}</p>
      </div>
      {data.records.filter((r) => !r.open).length > 0 && (
        <div className="border-t border-[#E8DFD0] pt-3 space-y-1.5" data-testid="today-records">
          {data.records.filter((r) => !r.open).map((r) => (
            <div key={r.id} className="flex items-center justify-between text-sm">
              <span className="font-mono-kv flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" />{fmtTime(r.clock_in)} – {fmtTime(r.clock_out)}</span>
              <span className="text-stone-500">{fmtMin(r.worked_min, t)}{r.overtime_min > 0 && <b className="text-orange-700"> · +{fmtMin(r.overtime_min, t)}</b>}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const TaskList = ({ period, tasks, onToggle }) => {
  const { t, lang } = useT();
  const done = tasks.filter((x) => x.done).length;
  return (
    <Card className="p-5 sm:p-6" data-testid={`tasks-${period}`}>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-display text-2xl font-bold">{t(`periods.${period}`)}</h2>
        <span className="text-xs text-stone-500" data-testid={`tasks-${period}-progress`}>{t("tasks.progress", { done, total: tasks.length })}</span>
      </div>
      <Bar done={done} total={tasks.length} color={period === "morning" ? "bg-[#b8563a]" : "bg-[#46634c]"} />
      <ul className="mt-4 space-y-2">
        {tasks.length === 0 && <li className="text-sm text-stone-500">{t("tasks.empty")}</li>}
        {tasks.map((task) => (
          <li key={task.id}>
            <button onClick={() => onToggle(task)} data-testid={`task-toggle-${task.id}`}
              className={`w-full flex items-start gap-3 text-left rounded-xl border px-4 py-3.5 transition-colors ${task.done ? "bg-[#f4f7f4] border-[#cddccf]" : "bg-white border-[#E8DFD0] hover:border-[#c86d51]"}`}>
              <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-lg border-2 grid place-items-center transition-colors ${task.done ? "bg-[#46634c] border-[#46634c] text-white" : "border-stone-300"}`}>
                {task.done && <Check className="h-4 w-4" />}
              </span>
              <span className="flex-1">
                <span className={`block text-[15px] ${task.done ? "line-through text-stone-500" : "text-[#2c1810]"}`}>{taskTitle(task, lang)}</span>
                {task.user_id && <span className="inline-block mt-1 rounded-full bg-[#fbe9dc] text-[#99432b] text-[11px] font-semibold px-2 py-0.5" data-testid={`task-personal-${task.id}`}>{t("tasks.personal")}</span>}
                {task.done && <span className="block text-xs text-[#46634c] mt-0.5 font-mono-kv">{t("tasks.done_at", { time: fmtTime(task.completed_at) })}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default function StaffToday() {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/me/today").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e, t)));
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (task) => {
    try {
      const { data: res } = await api.post(`/me/tasks/${task.id}/toggle`);
      setData((d) => ({ ...d, tasks: d.tasks.map((x) => (x.id === task.id ? { ...x, ...res } : x)) }));
      toast.success(res.done ? t("tasks.marked") : t("tasks.unmarked"));
    } catch (e) {
      toast.error(errMsg(e, t));
    }
  };

  if (!data) return <Loader />;
  return (
    <div className="space-y-5">
      <div className="kv-rise">
        <div className="font-mono-kv text-xs uppercase tracking-[0.2em] text-[#99432b]">{fmtDate(data.date, lang, { weekday: "long", day: "numeric", month: "long" })}</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1" data-testid="staff-greeting">{user.name}</h1>
        <div className="mt-2"><PositionBadge position={user.position} /></div>
      </div>
      <ShiftCard entry={data.entry} />
      <ClockCard data={data} onDone={load} />
      <RequestBox />
      {["morning", "evening"].map((p) => (
        <TaskList key={p} period={p} tasks={data.tasks.filter((x) => x.period === p)} onToggle={toggle} />
      ))}
    </div>
  );
}
